import type { SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement>

const base: IconProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  viewBox: '0 0 24 24',
  'aria-hidden': true,
}

export function EyeIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

export function EyeOffIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M10.7 6.1A8.9 8.9 0 0 1 12 6c6 0 9.5 6 9.5 6a17 17 0 0 1-2.7 3.4M6.3 7.6A16.7 16.7 0 0 0 2.5 12S6 18 12 18a9 9 0 0 0 3.5-.7" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" />
    </svg>
  )
}

export function SunIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2m0 16v2M2 12h2m16 0h2M4.9 4.9l1.5 1.5m11.2 11.2 1.5 1.5M19.1 4.9l-1.5 1.5M6.4 17.6l-1.5 1.5" />
    </svg>
  )
}

export function MoonIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  )
}

export function LogoutIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M15 17l5-5-5-5M20 12H9M12 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6" />
    </svg>
  )
}

export function ShieldIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M12 3l7 3v5.5c0 4.4-3 8-7 9.5-4-1.5-7-5.1-7-9.5V6l7-3Z" />
      <path d="m9.5 12 1.8 1.8 3.4-3.6" />
    </svg>
  )
}

export function LockIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <rect x="4.5" y="10.5" width="15" height="9.5" rx="2.5" />
      <path d="M8 10.5V7.75a4 4 0 0 1 8 0v2.75" />
    </svg>
  )
}

export function AlertIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5m0 3.5v.01" />
    </svg>
  )
}

export function PaymentsIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <rect x="2.5" y="5.5" width="19" height="13" rx="3" />
      <path d="M2.5 10h19" />
      <path d="M6.5 14.5h3" />
    </svg>
  )
}

export function ArrowLeftIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M19 12H5m6-7-7 7 7 7" />
    </svg>
  )
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M5 12h14m-6-7 7 7-7 7" />
    </svg>
  )
}

export function PlusIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

/** A card of ingredient lines. Marks a recipe — what goes into an item. */
export function RecipeIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <rect x="5" y="3.5" width="14" height="17" rx="2" />
      <path d="M9 8.5h6M9 12h6M9 15.5h3.5" />
    </svg>
  )
}

/** A tray with an arrow into it. Paired with Export, never with Upload. */
export function DownloadIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M12 3v12m0 0 4-4m-4 4-4-4" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  )
}

/** The download tray with the arrow reversed. Paired with Import, never with Export. */
export function UploadIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M12 15V3m0 0 4 4m-4-4-4 4" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  )
}

export function LinkIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M10 13.5a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.2 1.2" />
      <path d="M14 10.5a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.2-1.2" />
    </svg>
  )
}

export function UnlinkIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M14.5 9.5l1.2-1.2a4 4 0 0 1 5.7 5.7l-3 3a4 4 0 0 1-5.7 0" />
      <path d="M9.5 14.5l-1.2 1.2a4 4 0 0 1-5.7-5.7l3-3a4 4 0 0 1 5.7 0" />
    </svg>
  )
}

/** A bare tick, for inside a pill or a chip where a circle would crowd the label. */
export function CheckIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  )
}

export function CheckCircleIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <circle cx="12" cy="12" r="9.25" />
      <path d="m8 12.3 2.7 2.7L16 9.7" />
    </svg>
  )
}

export function ExternalLinkIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M13.5 4.5H19.5V10.5" />
      <path d="M19.5 4.5 11 13" />
      <path d="M18 14.5v4a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6h4" />
    </svg>
  )
}

export function QrCodeIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <rect x="3.5" y="3.5" width="6.5" height="6.5" rx="1.5" />
      <rect x="14" y="3.5" width="6.5" height="6.5" rx="1.5" />
      <rect x="3.5" y="14" width="6.5" height="6.5" rx="1.5" />
      <path d="M14 14h3v3h-3zM20.5 14v.01M20.5 20.5v.01M14 20.5v.01M17.5 17.5v.01" />
    </svg>
  )
}

export function CalendarIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 9.5h17M8 3.5V6m8-2.5V6" />
    </svg>
  )
}

/**
 * A shop front — awning over a door.
 *
 * Storefront shared `SettingsIcon` with Business, which made the two tiles read
 * as the same tool twice. This is the one that faces the customer.
 */
export function ShopIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M3.5 9.5V19a1.5 1.5 0 0 0 1.5 1.5h14a1.5 1.5 0 0 0 1.5-1.5V9.5" />
      <path d="M2.5 7.2 4.1 4.3a1.5 1.5 0 0 1 1.3-.8h13.2a1.5 1.5 0 0 1 1.3.8l1.6 2.9" />
      <path d="M2.5 7.2a2.6 2.6 0 0 0 4.8 1.4 2.6 2.6 0 0 0 4.7 0 2.6 2.6 0 0 0 4.7 0 2.6 2.6 0 0 0 4.8-1.4" />
      <path d="M9.5 20.5v-5.3a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v5.3" />
    </svg>
  )
}

export function SettingsIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 14.5a1.6 1.6 0 0 0 .3 1.8l.1.1a1.9 1.9 0 1 1-2.7 2.7l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.3a1.9 1.9 0 1 1-3.8 0v-.2a1.6 1.6 0 0 0-2.8-1.1l-.1.1a1.9 1.9 0 1 1-2.7-2.7l.1-.1a1.6 1.6 0 0 0-1.1-2.7h-.3a1.9 1.9 0 1 1 0-3.8h.2a1.6 1.6 0 0 0 1.1-2.8l-.1-.1a1.9 1.9 0 1 1 2.7-2.7l.1.1a1.6 1.6 0 0 0 2.7-1.1v-.3a1.9 1.9 0 1 1 3.8 0v.2a1.6 1.6 0 0 0 2.8 1.1l.1-.1a1.9 1.9 0 1 1 2.7 2.7l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.3a1.9 1.9 0 1 1 0 3.8h-.2a1.6 1.6 0 0 0-1.4.9Z" />
    </svg>
  )
}

export function PointOfSaleIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M6 2.5h12a.5.5 0 0 1 .5.5v18l-2.2-1.5-2.1 1.5-2.2-1.5-2.2 1.5-2.1-1.5L5.5 21V3a.5.5 0 0 1 .5-.5Z" />
      <path d="M9 7.5h6M9 11.5h6M9 15.5h3" />
    </svg>
  )
}

export function OrdersIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M9 4.5H7a2 2 0 0 0-2 2V19a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6.5a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="2.5" width="6" height="4" rx="1.25" />
      <path d="M8.75 11.5h6.5M8.75 15.5h4" />
    </svg>
  )
}

export function ProductsIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M11.3 2.9 2.9 11.3a1.5 1.5 0 0 0 0 2.1l7.7 7.7a1.5 1.5 0 0 0 2.1 0l8.4-8.4a1.5 1.5 0 0 0 .4-1.2l-.7-7a1.5 1.5 0 0 0-1.3-1.3l-7-.7a1.5 1.5 0 0 0-1.2.4Z" />
      <path d="M16.5 7.5v.01" />
    </svg>
  )
}

export function InventoryIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M20.5 8.2v7.6a1.5 1.5 0 0 1-.8 1.3l-6.9 3.7a1.5 1.5 0 0 1-1.6 0l-6.9-3.7a1.5 1.5 0 0 1-.8-1.3V8.2a1.5 1.5 0 0 1 .8-1.3l6.9-3.7a1.5 1.5 0 0 1 1.6 0l6.9 3.7a1.5 1.5 0 0 1 .8 1.3Z" />
      <path d="m3.9 7.4 8.1 4.3 8.1-4.3M12 20.8v-9.1" />
    </svg>
  )
}

export function PromotionsIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M12.6 2.8H19a2.2 2.2 0 0 1 2.2 2.2v6.4a2.2 2.2 0 0 1-.65 1.56l-7.4 7.4a2.2 2.2 0 0 1-3.1 0l-5.85-5.85a2.2 2.2 0 0 1 0-3.1l7.4-7.4a2.2 2.2 0 0 1 1.56-.65Z" />
      <path d="M16.8 7.2v.01" />
    </svg>
  )
}

export function CustomersIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.75 19.5a6.25 6.25 0 0 1 12.5 0" />
      <path d="M16 5.1a3.5 3.5 0 0 1 0 6.8M17.5 14.4a6.25 6.25 0 0 1 3.75 5.1" />
    </svg>
  )
}

export function StaffIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.75 20a7.25 7.25 0 0 1 14.5 0" />
    </svg>
  )
}

export function DeliveryIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M2.5 6.5h9.5v10H2.5z" />
      <path d="M12 9.5h3.6a2 2 0 0 1 1.7.95l2.2 3.55v2.5H12z" />
      <circle cx="7" cy="18.5" r="2" />
      <circle cx="16.5" cy="18.5" r="2" />
    </svg>
  )
}

export function ReportsIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M4.5 20.5V13m5 7.5V6m5 14.5v-5.5m5 5.5V9.5" />
    </svg>
  )
}

/* -------------------------------------------------------------------------- */
/* Report icons — one per card in the Reports catalogue                       */
/* -------------------------------------------------------------------------- */

/* Each says what its report answers, so a manager scanning a section finds the
   card by its picture before reading its name. Drawn lighter than the nav
   icons: they sit beside a title and a line of text, not on their own. */

const report: IconProps = { ...base, strokeWidth: 1.5, width: 18, height: 18 }

export function ReceiptIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
      <path d="M14 8H8M16 12H8M13 16H8" />
    </svg>
  )
}

export function ListTreeIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M21 12h-8M21 6H8M21 18h-8M3 6v4c0 1.1.9 2 2 2h3M3 10v6c0 1.1.9 2 2 2h3" />
    </svg>
  )
}

export function IdCardIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <circle cx="9" cy="11" r="2" />
      <path d="M16 10h2M16 14h2M6.17 15a3 3 0 0 1 5.66 0" />
    </svg>
  )
}

export function MapPinIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M20 10c0 5-5.54 10.19-7.4 11.8a1 1 0 0 1-1.2 0C9.54 20.19 4 15 4 10a8 8 0 0 1 16 0" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

export function CalendarDaysIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M8 2v4M16 2v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
    </svg>
  )
}

export function CalendarClockIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5M16 2v4M8 2v4M3 10h5" />
      <circle cx="16" cy="16" r="6" />
      <path d="M16 14v2.3l1.5 1.2" />
    </svg>
  )
}

export function CalendarHeartIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M3 10h18V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7M8 2v4M16 2v4" />
      <path d="M21.29 14.7a2.43 2.43 0 0 0-2.65-.52c-.3.12-.57.3-.8.53l-.34.34-.35-.34a2.43 2.43 0 0 0-2.65-.53c-.3.12-.56.3-.79.53-.95.94-1 2.53.2 3.74L17.5 22l3.6-3.55c1.2-1.21 1.14-2.8.19-3.74Z" />
    </svg>
  )
}

export function CalendarRangeIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18M17 14h-6M13 18H7M7 14h.01M17 18h.01" />
    </svg>
  )
}

export function CalendarCheckIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M8 2v4M16 2v4M3 10h18m-12 6 2 2 4-4" />
    </svg>
  )
}

export function CalendarSearchIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M21 11.75V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7.25M16 2v4M8 2v4M3 10h18m1 12-1.88-1.88" />
      <circle cx="18" cy="18" r="3" />
    </svg>
  )
}

export function RowsIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M21 9H3M21 15H3" />
    </svg>
  )
}

export function SplitIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M16 3h5v5M8 3H3v5M12 22v-8.3a4 4 0 0 0-1.17-2.87L3 3m12 6 6-6" />
    </svg>
  )
}

export function LayoutGridIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  )
}

export function UserCheckIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <circle cx="9" cy="7" r="4" />
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m14-10 2 2 4-4" />
    </svg>
  )
}

export function BadgePercentIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
      <path d="m15 9-6 6M9 9h.01M15 15h.01" />
    </svg>
  )
}

export function TrendLineIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M16 7h6v6m0-6-8.5 8.5-5-5L2 17" />
    </svg>
  )
}

export function CoinsIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <circle cx="8" cy="8" r="6" />
      <path d="M18.09 10.37A6 6 0 1 1 10.34 18M7 6h1v4m8.71 3.88.7.71-2.82 2.82" />
    </svg>
  )
}

export function UndoIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M9 14 4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
    </svg>
  )
}

export function BasketIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="m15 11-1 9m5-9-4-7M2 11h20M3.5 11l1.6 7.4a2 2 0 0 0 2 1.6h9.8a2 2 0 0 0 2-1.6l1.7-7.4M4.5 15.5h15M5 11l4-7m0 7 1 9" />
    </svg>
  )
}

export function AwardIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <circle cx="12" cy="8" r="6" />
      <path d="m15.48 12.89 1.51 8.53a.5.5 0 0 1-.81.47l-3.58-2.69a1 1 0 0 0-1.2 0l-3.59 2.69a.5.5 0 0 1-.81-.47l1.51-8.53" />
    </svg>
  )
}

export function CalculatorIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M8 6h8M16 14v4M16 10h.01M12 10h.01M8 10h.01M12 14h.01M8 14h.01M12 18h.01M8 18h.01" />
    </svg>
  )
}

export function ChefHatIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.46.32-.84.73-1.04a4 4 0 0 0-2.14-7.59 5 5 0 0 0-9.18 0 4 4 0 0 0-2.14 7.59c.41.2.73.58.73 1.04V20a1 1 0 0 0 1 1ZM6 17h12" />
    </svg>
  )
}

export function ArrowUpDownIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="m21 16-4 4-4-4m4 4V4M3 8l4-4 4 4M7 4v16" />
    </svg>
  )
}

export function ClipboardCheckIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2m1 10 2 2 4-4" />
    </svg>
  )
}

export function PackageMinusIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M16 16h6M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14M7.5 4.27l9 5.15M3.29 7 12 12l8.71-5M12 22V12" />
    </svg>
  )
}

export function ArrowRightLeftIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="m16 3 4 4-4 4m4-4H4m4 14-4-4 4-4m-4 4h16" />
    </svg>
  )
}

export function CookingPotIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="M2 12h20M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8M4 8l16-4M8.86 6.78l-.45-1.81a2 2 0 0 1 1.45-2.43l1.94-.48a2 2 0 0 1 2.43 1.46l.45 1.8" />
    </svg>
  )
}

export function ScaleIcon(props: IconProps) {
  return (
    <svg {...report} {...props}>
      <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1ZM2 16l3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1ZM7 21h10M12 3v18M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
    </svg>
  )
}

export function AnalyticsIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M12 3.5a8.5 8.5 0 1 0 8.5 8.5H12z" />
      <path d="M15 2.9a8.5 8.5 0 0 1 6.1 6.1H15z" />
    </svg>
  )
}

export function PurchasingIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M2.5 3.5h2.2l2.1 10.4a1.6 1.6 0 0 0 1.6 1.3h8.1a1.6 1.6 0 0 0 1.6-1.25L19.5 7H5.7" />
      <circle cx="9.5" cy="19.5" r="1.4" />
      <circle cx="16.5" cy="19.5" r="1.4" />
    </svg>
  )
}

export function MoneyIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <circle cx="12" cy="12" r="9.25" />
      <path d="M12 6.5v11M14.6 9.2a2.7 2.7 0 0 0-2.6-1.5c-1.5 0-2.7.9-2.7 2.2s1.1 1.9 2.7 2.2 2.7.9 2.7 2.2-1.2 2.2-2.7 2.2a2.8 2.8 0 0 1-2.7-1.6" />
    </svg>
  )
}

export function BagIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M5.4 7.5h13.2l-1 12a1.6 1.6 0 0 1-1.6 1.5H8a1.6 1.6 0 0 1-1.6-1.5z" />
      <path d="M9 10V6.75a3 3 0 0 1 6 0V10" />
    </svg>
  )
}

export function TrendUpIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" fill="currentColor" stroke="none" {...props}>
      <path d="M12 6.5 19 17H5z" />
    </svg>
  )
}

export function TrendDownIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" fill="currentColor" stroke="none" {...props}>
      <path d="M12 17.5 5 7h14z" />
    </svg>
  )
}

export function BellIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M18 8.75a6 6 0 1 0-12 0c0 4.2-1.5 5.4-1.5 5.4a1 1 0 0 0 .8 1.6h13.4a1 1 0 0 0 .8-1.6S18 12.95 18 8.75Z" />
      <path d="M13.7 19a2 2 0 0 1-3.4 0" />
    </svg>
  )
}

export function InfoIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5m0-8.5v.01" />
    </svg>
  )
}

export function AlertTriangleIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M10.3 3.9 2.7 17a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9.5v4m0 3v.01" />
    </svg>
  )
}

export function TrashIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M4 7h16M10 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" />
      <path d="M6 7v12a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 18 19V7" />
      <path d="M10 11v6m4-6v6" />
    </svg>
  )
}

export function PencilIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
      <path d="M14.5 6.5l3 3" />
    </svg>
  )
}

export function MoreIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  )
}

export function CloseIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  )
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="m15 5-7 7 7 7" />
    </svg>
  )
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="m9 5 7 7-7 7" />
    </svg>
  )
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <svg {...base} width="18" height="18" {...props}>
      <path d="m5 9 7 7 7-7" />
    </svg>
  )
}
